// Resolver una POSE o un FONDO guardado a la foto que va como referencia de generación —
// ADR-2026-09-14-la-pose-y-el-fondo-son-referencias-por-id.
//
// Hasta acá el agente podía nombrar por id un producto, un modelo, el logo o una creación
// del chat, pero NO una pose ni un fondo: la única forma de que viajaran era que el
// usuario los enganchara con el "+" en ese mismo mensaje. Eso dejaba afuera tres cosas
// que el usuario pide seguido: que el agente elija una pose de las que él tiene, que la
// REUSE en un mensaje posterior, y que un lote lleve UNA POSE DISTINTA POR FOTO.
//
// Vive en su propio módulo porque lo llaman los DOS caminos que arman referencias (la
// card de `generate_image` en `ChatPanelV2` y la del lote en `LoteImagenesCard`): si cada
// uno resolviera por su cuenta, uno de los dos se iba a quedar atrás.
import {
  fetchBibliotecaFondos,
  fetchFondosGuardados,
  signFondoGuardadoFull,
} from './fondosApi';
import { fetchAllPoses } from './posesApi';
import { fetchMyCustomPoses } from './posesCustomApi';

/**
 * Cache por empresa y por tipo, para lo que dura la pestaña.
 *
 * Un lote de 30 ítems que piden pose resolvería 30 veces el mismo catálogo. Los catálogos
 * de poses y fondos cambian cuando el usuario sube uno —un evento raro y suyo—, así que
 * una lectura por sesión alcanza; lo que NO se cachea son las URLs firmadas de los fondos
 * guardados, que se firman en cada resolución (ver abajo).
 */
const cache = new Map<string, Promise<Map<string, unknown>>>();

/** Vacía la cache (el usuario subió una pose o un fondo nuevo). */
export function olvidarCatalogoDeReferencias(): void {
  cache.clear();
}

const conCache = <T,>(clave: string, traer: () => Promise<Map<string, T>>): Promise<Map<string, T>> => {
  const guardado = cache.get(clave);
  if (guardado) return guardado as Promise<Map<string, T>>;
  // Se guarda la PROMESA, no el resultado: dos ítems que resuelven a la vez comparten la
  // misma lectura en vez de disparar dos. Si falla, se olvida para poder reintentar.
  const promesa = traer().catch((err) => {
    cache.delete(clave);
    throw err;
  });
  cache.set(clave, promesa as Promise<Map<string, unknown>>);
  return promesa;
};

/** Lo que el catálogo sabe de una referencia: su foto y CÓMO SE LLAMA. El nombre no es
 *  decorado: el agente elige poses y fondos de una lista de nombres, y cuando los mira
 *  necesita saber cuál está viendo (ADR-2026-09-22-mirar-una-imagen-no-depende-de-su-tipo). */
export interface ReferenciaDelCatalogo {
  url: string;
  nombre: string;
}

/** id → foto y nombre de todas las poses que el usuario puede usar: las suyas y las del
 *  catálogo. */
async function posesPorId(companyId: string): Promise<Map<string, ReferenciaDelCatalogo>> {
  return conCache(`poses:${companyId}`, async () => {
    const [propias, catalogo] = await Promise.all([
      fetchMyCustomPoses(companyId).catch(() => []),
      fetchAllPoses().catch(() => []),
    ]);
    const m = new Map<string, ReferenciaDelCatalogo>();
    // Las propias PRIMERO: si un id se repitiera entre las dos listas, gana la del usuario.
    for (const p of [...propias, ...catalogo]) {
      if (p.imageUrl) m.set(String(p.id), { url: p.imageUrl, nombre: p.nombre });
    }
    return m;
  });
}

/**
 * La pose guardada (foto y nombre), o null si ese id no existe.
 *
 * Las poses propias vienen firmadas por una hora desde `fetchMyCustomPoses`; como la
 * cache vive lo que dura la pestaña, una firma cacheada podría llegar vencida a una
 * generación tardía. No se re-firma acá: quien arma el pedido pasa TODAS las referencias
 * por `conFirmasFrescas`, que renueva lo que esté por vencer
 * (ADR-2026-09-12-las-fotos-viajan-con-firma-fresca).
 */
export async function poseGuardadaPorId(
  poseId: string,
  companyId: string,
): Promise<ReferenciaDelCatalogo | null> {
  if (!poseId || !companyId) return null;
  return (await posesPorId(companyId)).get(String(poseId)) ?? null;
}

/** Sólo la foto — el camino de siempre para armar una referencia de generación. */
export async function urlDePoseGuardada(
  poseId: string,
  companyId: string,
): Promise<string | null> {
  return (await poseGuardadaPorId(poseId, companyId))?.url ?? null;
}

/** id → nombre de archivo y nombre visible de los fondos GUARDADOS (los que hay que
 *  firmar al usarlos). */
async function fondosGuardadosPorId(
  companyId: string,
): Promise<Map<string, { fileName: string; nombre: string }>> {
  return conCache(`fondos-guardados:${companyId}`, async () => {
    const guardados = await fetchFondosGuardados(companyId).catch(() => []);
    const m = new Map<string, { fileName: string; nombre: string }>();
    for (const f of guardados) {
      if (f.fileName) m.set(String(f.id), { fileName: f.fileName, nombre: f.nombre });
    }
    return m;
  });
}

/** id → foto y nombre de los fondos de la BIBLIOTECA (sus URLs son públicas y no caducan). */
async function fondosDeBibliotecaPorId(): Promise<Map<string, ReferenciaDelCatalogo>> {
  return conCache("fondos-biblioteca", async () => {
    // Sin filtro de rubro: acá se resuelve un id que el usuario YA eligió; filtrar por
    // rubro sólo podría hacer que no se encuentre.
    const biblioteca = await fetchBibliotecaFondos(null).catch(() => []);
    const m = new Map<string, ReferenciaDelCatalogo>();
    for (const f of biblioteca) {
      if (f.imageUrl) m.set(String(f.id), { url: f.imageUrl, nombre: f.nombre });
    }
    return m;
  });
}

/**
 * El fondo (foto y nombre), o null si ese id no existe. Busca primero entre los GUARDADOS
 * del usuario y después en la biblioteca.
 *
 * El guardado se firma en su tamaño ORIGINAL en cada resolución, no se cachea: la lista
 * trae una miniatura transformada, y una miniatura es una referencia pobre para el motor
 * (ADR-2026-09-09-las-referencias-van-en-full).
 */
export async function fondoGuardadoPorId(
  fondoId: string,
  companyId: string,
): Promise<ReferenciaDelCatalogo | null> {
  if (!fondoId) return null;
  if (companyId) {
    const guardado = (await fondosGuardadosPorId(companyId)).get(String(fondoId));
    if (guardado) {
      const full = await signFondoGuardadoFull(companyId, guardado.fileName);
      if (full) return { url: full, nombre: guardado.nombre };
    }
  }
  return (await fondosDeBibliotecaPorId()).get(String(fondoId)) ?? null;
}

/** Sólo la foto — el camino de siempre para armar una referencia de generación. */
export async function urlDeFondoGuardado(
  fondoId: string,
  companyId: string,
): Promise<string | null> {
  return (await fondoGuardadoPorId(fondoId, companyId))?.url ?? null;
}
